import { forwardingUrl } from "helpers/forwarding";

type RouteID = string | number;

const paths = {
    mobileApps: (tab: string) => `/mobile-apps?tab=${tab}`,

    forwarding: forwardingUrl(),
    forwardingLogin: `${forwardingUrl()}/cargorunAuth`,
    forwardingAuth: `/authorization/expedition`,
    forwardingOrganizationLogo: (organizationId: RouteID) =>
        `${forwardingUrl()}/api/Organization/GetLogo?organizationId=${organizationId}`,
    forwardingTruckerPlanning: `${forwardingUrl()}/truckerPlanning`,
    forwardingSuitableOrder: `/suitable-orders/forwarding`,

    domain: "/",
    root: "/monitoring",
    login: "/login",
    registration: "/registration",
    registrationAtiSu: "/registration/atisu",
    registrationCallbackAtiSu: "/registration/callback/atisu",
    registrationCreateRelay: "/registration/create-relay",
    registrationAddCars: "/registration/add-cars",
    registrationAddDrivers: "/registration/add-drivers",
    registrationAddTrucks: "/registration/add-trucks",
    forgotPassword: "/account/forgotPassword",
    resetPassword: "/account/resetPassword",
    confirmEmail: "/account/confirmEmail",
    acceptInvitation: "/account/acceptInvitation",
    unsubscribe: "/notifications/unsubscribe",
    licenseExpired: "/license-expired",

    home: "/home",
    error: "/home/error",
    ip: "/home/ip",
    headers: "/home/headers",
    ping: "/home/ping",

    connectKontur: "/organization/connect-kontur",

    dashboard: "/dashboard",
    cargorunIndex: "/ftl-index",
    assistant: "/assistant",

    bid: "/bids",
    bidNew: "/bids/new",
    bidShow: (type: "bid" | "distributionBid", id: RouteID) => `/bids/${type}/${id}`,
    bidEdit: (id: RouteID) => `/bids/edit/${id}`,

    epd: "/epd",
    shippingOrders: "/epd/shipping-orders",
    shippingOrdersShow: (id: RouteID) => `/epd/shipping-orders/${id}`,
    waybills: "/epd/waybills",
    waybillsShow: (id: RouteID) => `/epd/waybills/${id}`,

    chats: "/chats",
    chatsShow: (id: RouteID) => `/chats/list/${id}`,

    carChats: "/car-chats",
    carChatsShow: (id: RouteID) => `/car-chats/${id}`,

    notifications: "/notifications",
    notificationsShow: (id: RouteID) => `/notifications/list/${id}`,

    drivers: "/drivers",
    driversNew: "/drivers/new",
    driversShow: (id: RouteID) => `/drivers/list/${id}`,

    distributionBids: "/distribution-bids",
    distributionBidsNew: "/distribution-bids/list/new",
    distributionBidShow: (id: RouteID) => `/distribution-bids/bid/${id}`,
    distributionBidEdit: (id: RouteID) => `/distribution-bids/edit/${id}`,

    trailers: "/trailers",
    trailersNew: "/trailers/new",
    trailersShow: (id: RouteID) => `/trailers/list/${id}`,

    transportColumns: "/transport-columns",
    transportColumnsNew: "/transport-columns/new",
    transportColumnsShow: (id: RouteID) => `/transport-columns/list/${id}`,

    counterpartyGroups: "/counterparty-groups",
    counterpartyGroupsNew: "/counterparty-groups/new",
    counterpartyGroupsShow: (id: RouteID) => `/counterparty-groups/${id}`,

    trucks: "/trucks",
    trucksNew: "/trucks/new",
    trucksShow: (id: RouteID) => `/trucks/list/${id}`,
    trucksAddLogist: "/trucks/add-logist",

    // Hired park
    hiredPark: "/hired-park",
    hiredCars: "/hired-park/cars",
    hiredCarsNew: "/hired-park/cars/new",
    hiredCarsEdit: (id: RouteID) => `/hired-park/cars/${id}/edit`,
    hiredCarsShow: (id: RouteID) => `/hired-park/cars/${id}`,

    hiredTrailers: "/hired-park/trailers",
    hiredTrailersNew: "/hired-park/trailers/new",
    hiredTrailersEdit: (id: RouteID) => `/hired-park/trailers/${id}/edit`,
    hiredTrailersShow: (id: RouteID) => `/hired-park/trailers/${id}`,

    hiredDrivers: "/hired-park/drivers",
    hiredDriversNew: "/hired-park/drivers/new",
    hiredDriversEdit: (id: RouteID) => `/hired-park/drivers/${id}/edit`,
    hiredDriversShow: (id: RouteID) => `/hired-park/drivers/${id}`,

    calculationQueues: "/admin/calculation-queues",
    calculationQueuesNew: "/admin/calculation-queues/list/new",

    cargoOwners: "/cargo-owners",
    cargoOwnersNew: "/cargo-owners/new",
    cargoOwnersShow: (id: RouteID) => `/cargo-owners/${id}`,

    counterpartyPoints: (counterpartyId: RouteID) => `/cargo-owners/${counterpartyId}/points`,
    counterpartyPointsNew: (counterpartyId: RouteID) => `/cargo-owners/${counterpartyId}/points/new`,
    counterpartyPointShow: (counterpartyId: RouteID, id: RouteID) => `/cargo-owners/${counterpartyId}/points/${id}`,

    counterpartyContracts: (counterpartyId: RouteID) => `/cargo-owners/${counterpartyId}/contracts`,
    counterpartyContractsNew: (counterpartyId: RouteID) => `/cargo-owners/${counterpartyId}/contracts/new`,
    counterpartyContractsShow: (counterpartyId: RouteID, id: RouteID) =>
        `/cargo-owners/${counterpartyId}/contracts/${id}`,

    dictionaries: "/dictionaries",
    dictionariesShow: (id: RouteID) => `/dictionaries/list/${id}`,

    trackers: "/trackers",
    trackersNew: "/trackers/new",
    trackersShow: (id: RouteID) => `/trackers/list/${id}`,

    trackerHistory: (trackerId: RouteID) => `/trackers/list/${trackerId}/history`,
    trackerHistoryNew: (trackerId: RouteID) => `/trackers/list/${trackerId}/history/new`,
    trackerHistoryItem: (trackerId: RouteID, id: RouteID) => `/trackers/list/${trackerId}/history/${id}`,

    trackerOdometerValues: (trackerId: RouteID) => `/trackers/list/${trackerId}/odometer-values`,

    logistHistory: (truckId: RouteID) => `/trucks/list/${truckId}/history`,
    logistHistoryNew: (truckId: RouteID) => `/trucks/list/${truckId}/history/new`,
    logistHistoryItem: (truckId: RouteID, id: RouteID) => `/trucks/list/${truckId}/history/${id}`,

    customPoints: "/custom-points",
    customPointsNew: "/custom-points/new",
    customPointsShow: (id: RouteID) => `/custom-points/list/${id}`,

    itemChangePoints: "/item-change-points",
    itemChangePointsShow: (id: RouteID) => `/item-change-points/${id}`,

    regionGroups: "/region-groups",
    regionGroupsNew: "/region-groups/new",
    regionGroupsShow: (id: RouteID) => `/region-groups/${id}`,

    reports: "/reports",
    reportsShow: (id: RouteID) => `/reports/${id}`,

    planning: "/planning",
    planningShow: (id: RouteID) => `/planning/${id}`,

    performancePlans: "/performance-plans",
    performancePlansHistory: "/performance-plans/history",
    performancePlansSettings: "/performance-plans/settings",

    profile: "/profile",
    profileAccess: "/profile/access",
    profileAccessMembers: "/profile/access/members",
    profileAccessBlock: "/profile/access/blocked",
    profileAccessInvited: "/profile/access/invited",
    profileOrgData: "/profile/organization",
    profileEpdSettings: "/profile/epd-settings",
    profileConnectCars: "/profile/connect-cars",
    profileEntityOptions: "/profile/entityOptions",
    profileUserData: "/profile/profile",
    profileAccount: "/profile/account",
    profileEducationResources: "/profile/education-resources",
    profileTaskSettings: "/profile/task-settings",
    profileBilling: "/profile/billing",
    profileBillingDocuments: "/profile/billing/documents",
    profileBillingDocumentDownload: (id: RouteID) => `/profile/billing/documents/${id}/download`,
    profileBillingCharges: "/profile/billing/charges",
    profileBillingPayments: "/profile/billing/payments",
    profileBillingNotifications: "/profile/billing/notifications",

    adminUsers: "/admin/users",
    userLogs: "/admin/user-logs",
    userLogsShow: (id: RouteID) => `/admin/user-logs/list/${id}`,

    adminTrackerLogs: "/admin/tracker-logs",

    adminFilterCoordinates: "/admin/filter-coordinates",

    adminDrivers: "/admin/drivers",
    adminDriverDeviceStatistics: "/admin/driver-device-statistics",

    adminOrganizations: "/admin/organizations",
    adminOrganizationsNew: "/admin/organizations/new",
    adminOrganizationsShow: (id: RouteID) => `/admin/organizations/list/${id}`,
    adminOrganizationModules: (id: RouteID) => `/admin/organizations/list/${id}/feature-modules`,
    adminOrganizationOdometerHistory: (id: RouteID) => `/admin/organizations/list/${id}/odometer-history`,
    adminOrganizationSettings: "/admin/organizations/settings",

    integrationClients: "/admin/integration-clients",
    integrationClientsNew: "/admin/integration-clients/new",
    integrationClient: (clientId: RouteID) => `/admin/integration-clients/${clientId}`,

    routeTemplates: "/route-templates",
    routeTemplatesShow: (id: RouteID) => `/route-templates/list/${id}`,

    emergencyAreaPoints: "/emergency-area-points",
    emergencyAreaPointsShow: (id: RouteID) => `/emergency-area-points/list/${id}`,
    emergencyAreaPointsNew: `/emergency-area-points/new`,

    routeControlPoints: "/route-control-points",
    routeControlPointsShow: (id: RouteID) => `/route-control-points/list/${id}`,
    routeControlPointsNew: `/route-control-points/new`,

    organizationCatalogs: "/organization-catalogs",
    organizationCatalogsShow: (id: RouteID) => `/organization-catalogs/list/${id}`,
    organizationCatalogItems: (id: RouteID) => `/organization-catalogs/list/${id}/items`,

    systemCatalogs: "/system-catalogs",
    systemCatalogsShow: (id: RouteID) => `/system-catalogs/list/${id}`,

    scenarios: "/scenarios",
    scenariosNew: "/scenarios/new",
    scenariosShow: (id: RouteID) => `/scenarios/list/${id}`,

    tripScenarios: "/trip-scenarios",
    tripScenariosNew: "/trip-scenarios/new",
    tripScenariosShow: (id: RouteID) => `/trip-scenarios/list/${id}`,

    trackersInfo: "/admin/trackers-info",

    couplings: "/couplings",

    usersGroups: "/users-groups",
    usersGroupsShow: (id: RouteID) => `/users-groups/${id}`,
    usersGroupCars: (groupId: RouteID) => `/users-groups/${groupId}/cars`,
    usersGroupUsers: (groupId: RouteID) => `/users-groups/${groupId}/users`,

    usersGroupsCars: "/users-groups-cars",

    news: "/news",
    newsNew: "/news/new",
    newsShow: (id: RouteID) => `/news/list/${id}`,

    postamatPoints: "/postamat-points",
    postamatPointsNew: "/postamat-points/new",
    postamatPointsShow: (id: RouteID) => `/postamat-points/${id}`,

    notificationQueues: "/admin/notification-queues",
    notificationQueueShow: (id: RouteID) => `/admin/notification-queues/list/${id}`,

    clientStatistics: "/admin/client-statistics",

    blockedEmails: "/admin/blocked-emails",

    distributionBidSources: "/distribution-bid-sources",

    fuelCards: "/fuel-cards",
    fuelCardsShow: (id: RouteID) => `/fuel-cards/list/${id}`,

    organizationActivityReport: "/admin/organization-activity-report",
    driversStatsOfYearReport: "/admin/driver-stats-of-year-report",
    fuelLimitsReport: "/admin/fuel-limits-report",
    suitableOrdersReport: "/admin/suitable-orders-report",

    requestStatistics: "/admin/request-statistics",

    fixRouteBids: "/admin/fix-route-bids",
    fixRouteBidsShow: (id: RouteID) => `/admin/fix-route-bids/list/${id}`,

    organizationFixRouteBids: "/fix-route-bids",
    organizationFixRouteBidsShow: (id: RouteID) => `/fix-route-bids/list/${id}`,

    neuralNetworkLogs: "/admin/neural-network-logs",

    organizationRecommendations: "/admin/organization-recommendations",

    mobileAppRatings: "/admin/mobile-app-ratings",

    organizationRatings: "/organization-ratings",
    assistantChatReport: (reportType: string, messageId: string) => `/assistantChat/report/${reportType}/${messageId}`,

    tasks: "/tasks",
    tasksKanban: "/tasks/kanban",
    tasksTable: "/tasks/table",
    tasksShow: (id: RouteID) => `/tasks/${id}`,

    legalPersons: "/legal-persons",
    legalPersonsNew: "/legal-persons/new",
    legalPersonsShow: (id: RouteID) => `/legal-persons/${id}`,

    popups: "/admin/popups",
    popupsShow: (id: RouteID) => `/admin/popups/list/${id}`,

    popupTemplates: "/admin/popup-templates",
    popupTemplatesShow: (id: RouteID) => `/admin/popup-templates/list/${id}`,

    platonPoints: "/platon-points",
    platonPointsNew: "/platon-points/new",
    platonPointsShow: (id: RouteID) => `/platon-points/list/${id}`,

    accessPermits: "/access-permits",
    adminAccessPermits: "/admin/access-permits",
    adminAccessPermitsNew: "/admin/access-permits/new",
    adminAccessPermitsShow: (id: RouteID) => `/admin/access-permits/list/${id}`,

    adminParcelStatuses: "/admin/parcel-statuses",
    adminParcelStatusesNew: "/admin/parcel-statuses/new",
    adminParcelStatusesShow: (id: RouteID) => `/admin/parcel-statuses/list/${id}`,

    adminPlatformNews: "/admin/platform-news",
    adminPlatformNewsNew: "/admin/platform-news/new",
    adminPlatformNewsShow: (id: RouteID) => `/admin/platform-news/list/${id}`,

    userPlatformNews: "/platform-news",
    userPlatformNewsShow: (id: RouteID) => `/platform-news/list/${id}`,

    paymentPoints: "/admin/payment-points",
    paymentPointsShow: (id: RouteID) => `/admin/payment-points/list/${id}`,
    paymentPointsNew: `/admin/payment-points/new`,

    regionDryingPeriods: "/admin/region-drying-periods",
    regionDryingPeriodsShow: (id: RouteID) => `/admin/region-drying-periods/list/${id}`,
    regionDryingPeriodsNew: `/admin/region-drying-periods/new`,

    emulatedSegmentsLogs: "/admin/emulated-segments-logs",
    preservedTrackerLogs: "/admin/preserved-tracker-logs",
    feedbackTags: "/admin/feedback-tags",

    unstableZones: "/admin/unstable-zones",

    integration: "/integration",
    integrationItem: (id: RouteID) => `/integration/${id}`,

    tripSheets: "/finances/trip-sheets",
    tripSheetsNew: "/finances/trip-sheets/new",
    tripSheetsShow: (id: RouteID) => `/finances/trip-sheets/${id}`,
    tripSheetsChangelog: (id: RouteID) => `/finances/trip-sheets/${id}/changelog`,

    finances: "/finances",
    financesShow: (id: RouteID) => `/finances/${id}`,
    financesInvoice: "/finances/invoice",
    financesContract: "/finances/contract",
    financesAcceptanceCertificate: "/finances/acceptance-certificate",
    financesAcceptanceCertificateInvoice: "/finances/acceptance-certificate-invoice",
    financesDocumentsShow: (id: RouteID) => `/finances/documents/${id}`,
    universalTransferDocuments: "/finances/universal-transfer-documents",
    universalTransferDocumentsShow: (id: RouteID) => `/finances/universal-transfer-documents/${id}`,
    registries: "/finances/registries",
    registriesShow: (id: RouteID) => `/finances/registries/${id}`,
    documentTracking: "/finances/document-tracking",

    documentTemplates: "/document-templates",
    documentTemplatesNew: "/document-templates/new",
    documentTemplatesShow: (id: RouteID) => `/document-templates/${id}`,
    documentNumberSettings: "/document-number-settings",

    classificationTypes: "/classification-types",
    classificationTypesNew: "/classification-types/new",
    classificationTypesShow: (id: RouteID) => `/classification-types/${id}`,

    expenses: "/finances/expenses",
    expensesShow: (id: RouteID) => `/finances/expenses/${id}`,

    adminTariffs: "/admin/tariffs",
    adminReportAllowedUsers: "/admin/report-allowed-users",
    adminClientStatisticsReport: "/admin/client-statistics-report",
    adminPlanFactOverallReport: "/admin/plan-fact-overall-report",
    adminInsolvencyRegistry: "/admin/insolvency-registry",
};

type PathsValues = (typeof paths)[keyof typeof paths];

export { paths };
export type { PathsValues };
